import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, Modal, Table, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  createPermission,
  fetchPermissions,
  updatePermission
} from '../../utils/users';

import { useIsMobile } from '../../hooks/useIsMobile';

const PermissionsTab = ({ isActive }) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const [form] = Form.useForm();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const permissionLabel = useCallback(
    (record) => {
      if (i18n.language === 'en') {
        return record.permission_name_en || record.permission_name_zh;
      }
      return record.permission_name_zh || record.permission_name_en;
    },
    [i18n.language]
  );

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      const res = await fetchPermissions();
      setPermissions(res.permissions || []);
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isActive) return;
    loadData();
  }, [isActive, loadData]);

  const openCreateModal = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEditModal = useCallback(
    (record) => {
      setEditing(record);
      form.setFieldsValue({
        permissionId: record.permission_id,
        permissionNameZh: record.permission_name_zh,
        permissionNameEn: record.permission_name_en,
        permissionNameVi: record.permission_name_vi,
        permissionNameTh: record.permission_name_th
      });
      setModalOpen(true);
    },
    [form]
  );

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      if (editing) {
        await updatePermission(editing.permission_id, {
          permissionNameZh: values.permissionNameZh,
          permissionNameEn: values.permissionNameEn,
          permissionNameVi: values.permissionNameVi,
          permissionNameTh: values.permissionNameTh
        });
        message.success(t('permissions.updateSuccess'));
      } else {
        await createPermission(values);
        message.success(t('permissions.createSuccess'));
      }

      closeModal();
      await loadData();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message || t('permissions.saveError'));
    } finally {
      setSubmitting(false);
    }
  };

  const columns = useMemo(
    () => [
      { title: t('permissions.permissionId'), dataIndex: 'permission_id', key: 'permission_id' },
      {
        title: t('permissions.name'),
        key: 'name',
        render: (_, record) => permissionLabel(record)
      },
      {
        title: t('permissions.actions'),
        key: 'actions',
        render: (_, record) => (
          <Button type="link" onClick={() => openEditModal(record)}>
            {t('permissions.edit')}
          </Button>
        )
      }
    ],
    [openEditModal, permissionLabel, t]
  );

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
          {t('permissions.add')}
        </Button>
      </div>

      <Table
        rowKey="permission_id"
        loading={loading}
        columns={columns}
        dataSource={permissions}
        pagination={{ pageSize: 10, size: isMobile ? 'small' : 'default' }}
        scroll={{ x: 'max-content' }}
        className="system-table"
      />

      <Modal
        title={editing ? t('permissions.editTitle') : t('permissions.addTitle')}
        open={modalOpen}
        onCancel={closeModal}
        onOk={handleSubmit}
        confirmLoading={submitting}
        destroyOnClose
        width={isMobile ? '100%' : 640}
        className="system-modal"
      >
        <Form form={form} layout="vertical" className="mt-4">
          <Form.Item
            name="permissionId"
            label={t('permissions.permissionId')}
            rules={[{ required: true, message: t('permissions.permissionIdRequired') }]}
          >
            <Input disabled={Boolean(editing)} placeholder={t('permissions.permissionIdPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="permissionNameZh"
            label={t('permissions.nameZh')}
            rules={[{ required: true, message: t('permissions.nameZhRequired') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="permissionNameEn"
            label={t('permissions.nameEn')}
            rules={[{ required: true, message: t('permissions.nameEnRequired') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="permissionNameVi"
            label={t('permissions.nameVi')}
            rules={[{ required: true, message: t('permissions.nameViRequired') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="permissionNameTh"
            label={t('permissions.nameTh')}
            rules={[{ required: true, message: t('permissions.nameThRequired') }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default PermissionsTab;
